import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Eye, Mail, FolderKanban, Search } from 'lucide-react';
import { userService } from '../../services/userService';
import { projectService } from '../../services/projectService';

const ClientsList = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientProjects, setClientProjects] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const clientsData = await userService?.getClientUsers();
      setClients(clientsData);

      // Load project count for each client
      const projectCounts = {};
      await Promise.all(
        clientsData?.map(async (client) => {
          try {
            const projects = await projectService?.getProjectsByClient(client?.id);
            projectCounts[client?.id] = projects?.length || 0;
          } catch (err) {
            projectCounts[client?.id] = 0;
          }
        })
      );
      setClientProjects(projectCounts);
      setError('');
    } catch (err) {
      setError('Erro ao carregar clientes: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClientProject = (clientId) => {
    navigate(`/client-project-view/${clientId}`);
  };

  const filteredClients = clients?.filter(client => 
    client?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    client?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-sm text-muted-foreground">Carregando clientes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-6 text-center">
        <p className="text-destructive font-medium">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3">
            <Users className="h-7 w-7 text-primary" />
            Clientes Cadastrados
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {filteredClients?.length} {filteredClients?.length === 1 ? 'cliente encontrado' : 'clientes encontrados'}
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
          />
        </div>
      </div>

      {/* Clients Grid */}
      {filteredClients?.length === 0 ? (
        <div className="text-center py-20 bg-muted/30 rounded-2xl border border-border">
          <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-semibold text-foreground mb-2">
            {searchTerm ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {searchTerm ? 'Tente ajustar os termos de busca' : 'Cadastre clientes para começar'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredClients?.map((client) => (
            <div
              key={client?.id}
              className="bg-card border border-border rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
            >
              {/* Client Avatar */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-foreground text-lg truncate">
                      {client?.fullName || 'Sem nome'}
                    </h3>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                      <Mail className="h-3.5 w-3.5" />
                      <span className="truncate">{client?.email}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Client Stats */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <FolderKanban className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Projetos</span>
                  </div>
                  <span className="text-lg font-bold text-primary">
                    {clientProjects?.[client?.id] || 0}
                  </span>
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={() => handleViewClientProject(client?.id)}
                disabled={!clientProjects?.[client?.id]}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  clientProjects?.[client?.id]
                    ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                }`}
              >
                <Eye className="h-4 w-4" />
                <span className="text-sm">
                  {clientProjects?.[client?.id] ? 'Ver Projetos do Cliente' : 'Sem Projetos'}
                </span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientsList;